import React from 'react';
import Select from 'react-select';
import Moment from 'moment';
import { prefix, t } from '../../../../i18n';
import CustomDataTable from '../../shared/CustomDataTable';
import KeyDropdownValue from '../../shared/KeyDropdownValue';
import CustomButton from '../../shared/CustomButton';
import { formatDate } from '../../normalizer';

const tAttribution = prefix('attributions');

const getUserOptions = (users) =>
  users.map((user) => ({ id: user.id, value: user.id, label: user.employee_identifier }));

const getContractNumber = (saleAttribution) => {
  const ef = saleAttribution.extra_fields.find(
    (ef) => ef.name.toLowerCase() === 'contractnumber'
  );
  return ef ? ef.value : '';
};

/**
 * Pending Attributions Table component using CustomDataTable
 */
const SaleTable = ({ attributions, users, onAccept, onOpenUpdate, page, pagination, onPageChange }) => {
  // Persist selected users across re-renders (local `let` resets on every render)
  const selectedUsersRef = React.useRef({});

  const handleUserChange = (attribution) => (e) => {
    selectedUsersRef.current[attribution.id] = e;
  };

  const columns = [
    {
      key: 'date',
      label: tAttribution('date'),
      sortKey: 'date',
      render: (_v, item) => formatDate(item.date),
    },
    {
      key: 'contractnumber',
      label: tAttribution('contractnumber'),
      sortKey: 'contractnumber',
      render: (_v, item) => getContractNumber(item),
    },
    {
      key: 'user',
      label: tAttribution('user'),
      sortKey: 'user',
      sortable: false,
      render: (_v, item) => (
        <div onClick={(e) => e.stopPropagation()}>
          <KeyDropdownValue
            className="pending-attributions-table-drop-select"
            options={getUserOptions(users)}
            optionsLabel="label"
            optionsId="id"
            onChange={handleUserChange(item)}
            placeholder="Selecione"
          />
        </div>
      ),
    },
    {
      key: 'actions',
      label: 'Ações',
      sortKey: 'actions',
      sortable: false,
      render: (_v, item) => {
        if (item.processing) return 'Em processamento';
        return (
          <div onClick={(e) => e.stopPropagation()}>
            <CustomButton link tight onClick={() => onOpenUpdate(item, selectedUsersRef.current[item.id])}>
              Atribuir contrato
            </CustomButton>
          </div>
        );
      },
    },
  ];

  const sortFn = (data, sort) => {
    return [...data].sort((a, b) => {
      const multiplier = sort.direction === 'asc' ? 1 : -1;
      if (sort.key === 'date') {
        return (new Date(a.date) - new Date(b.date)) * multiplier;
      }
      if (sort.key === 'contractnumber') {
        const valA = getContractNumber(a).toLowerCase();
        const valB = getContractNumber(b).toLowerCase();
        return valA.localeCompare(valB) * multiplier;
      }
      return 0;
    });
  };

  const handlePageChange = (pageNumber) => {
    onPageChange({ selected: pageNumber - 1 });
  };

  const renderRow = (attribution, index, className) => {
    const handleOnUserChange = (e) => {
      selectedUsersRef.current[attribution.id] = e;
    };

    const handleOnAccept = () => {
      if (!attribution.processing) {
        onAccept(attribution, selectedUsersRef.current[attribution.id]);
      }
    };

    const isProcessing = attribution.processing;

    return (
      <tr
        key={attribution.id || index}
        className={`data-table__row ${className}__row`}
        onClick={() => onOpenUpdate(attribution)}
        style={{ cursor: 'pointer' }}
      >
        <td className={`data-table__cell ${className}__cell ${className}__cell--date`}>
          {formatDate(attribution.date)}
        </td>
        <td className={`data-table__cell ${className}__cell ${className}__cell--contractnumber`}>
          {getContractNumber(attribution)}
        </td>
        <td className={`data-table__cell ${className}__cell ${className}__cell--user`} onClick={(e) => e.stopPropagation()}>
          <KeyDropdownValue
            className="pending-attributions-table-drop-select"
            options={getUserOptions(users)}
            optionsLabel="label"
            optionsId="id"
            onChange={handleOnUserChange}
            placeholder="Selecione"
          />
        </td>
        <td className={`data-table__cell ${className}__cell ${className}__cell--actions`} onClick={(e) => e.stopPropagation()}>
          {isProcessing ? (
            'Em processamento'
          ) : (
            <CustomButton link tight onClick={() => onOpenUpdate(attribution, selectedUsersRef.current[attribution.id])} >
              Atribuir contrato
            </CustomButton>
          )}
        </td>
      </tr>
    );
  };

  return (
    <CustomDataTable
      className="pending-attributions-table"
      columns={columns}
      data={attributions}
      renderRow={renderRow}
      rowKey={(item) => item.id}
      defaultSort={{ key: 'date', direction: 'desc' }}
      sortFn={sortFn}
      pagination={{
        totalPages: pagination.total_pages,
        currentPage: page,
        onPageChange: handlePageChange,
      }}
      emptyMessage="Não Existem contratos com erros de utilizador."
    />
  );
};

export default SaleTable;
